import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Booking, UserSettings } from '../Types';
import { RootState, useAppSelector } from '../store';
import { creditService } from '../Services/creditService';
import requestClient from '../components/Utility/RequestClient';
import { getBreakpoint } from '../utils/breakpoint';

const useBilling = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);

    const [bookings, setBookings] = useState<Booking[]>([]);
    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const [bookingsLoaded, setBookingsLoaded] = useState<boolean>(false);

    const valueFormatter = useCallback(
        (number: number | string) => {
            const parsedNumber = Number(number);
            if (isNaN(parsedNumber)) {
                console.warn('Invalid number passed to valueFormatter:', number);
                return '$ 0.00';
            }

            return `$ ${new Intl.NumberFormat(selectedLanguage, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(parsedNumber)}`;
        },
        [selectedLanguage],
    );

    useEffect(() => {
        creditService.getBooking().then((response: { data: { data: Booking[]; total: number } }) => {
            setBookings(() =>
                response.data.data.map((booking: Booking) => {
                    return {
                        identifier: booking.identifier,
                        title: booking.title,
                        amount: valueFormatter(booking.amount),
                        date: new Date(booking.date),
                    };
                }),
            );
            setBookingsLoaded(true);
        });
    }, [demo, valueFormatter, requestClient]);

    const BillingActions = useMemo(
        () => ({
            valueFormatter,
        }),
        [valueFormatter],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        BillingActions,
        bookings,
        selectedLanguage,
        userSettings,
        demo,
        i18n,
        bookingsLoaded,
        getBreakpoint,
        windowWidth,
        handleNavigate,
    };
};

export default useBilling;
